/**
 * Helpers for cleaning protocol-analyzer and Study Settings field values.
 *
 * The protocol analyzer often emits the literal string "Not specified" or
 * "Not Specified" when a field is missing from the protocol, and Study
 * Settings stores the choice "unknown" (shown as "Unknown / Not yet
 * decided") for a question nobody has answered yet. Neither is a value.
 * Treat them as empty so downstream rendering (title pages, headers,
 * metadata, prompts) falls back cleanly or says TBD, instead of printing a
 * placeholder word as though it were a fact about the study.
 */

/** Placeholder words that mean "no value", compared case-insensitively. */
const PLACEHOLDER =
  /^(not\s+specified|unspecified|unknown|unknown\s*\/\s*not\s+yet\s+decided|not\s+yet\s+decided|tbd|tba)$/i;
// "N/A", "NA" and "Not applicable" are NOT here: they are real answers (the
// phase of a device or behavioural study is legitimately "N/A").

/** True when `v` is a placeholder word rather than a value. */
export function isPlaceholderValue(v: string): boolean {
  return PLACEHOLDER.test(v.trim());
}

/**
 * Text that is a protocol VERSION or an IND/IDE number, not a protocol
 * number. A version is "version"/"ver." anything, or "v" + a number that has
 * a dot or is followed by a space or the end ("v2", "V 3.0", "v1.2 amended").
 * A V-prefixed alphanumeric CODE ("V903-217", "V2-ACME-7") is a real number.
 * IND / IDE text is refused in two shapes:
 *   - an IND or IDE NUMBER: "IND" or "IDE", then optionally a space, "#",
 *     "No." or ":", then a digit ("IND 148144", "IND123456", "IND #148144",
 *     "IND No. 148144", "IND: 148144"), or an IDE G-number ("IDE G123456");
 *   - "IND" or "IDE" (or "IND/IDE") alone, or followed by a space or
 *     punctuation and then words ("IND pending", "IND exempt",
 *     "IND/IDE: N/A", "IDE exempt (NSR device)"): the start of the value is
 *     not followed by a hyphen, letter or digit.
 * A code whose first part is joined to IND / IDE by a hyphen or letters is
 * kept: "IND-ABC-9", "ind-301", "IDE-2023-7", "INDIGO-301", "INDY-7",
 * "IDEAL-2".
 */
const NOT_A_PROTOCOL_NUMBER =
  /^\s*(?:version\b|ver\.|v\.?\s*\d+(?:\.\d+)+|v\.?\s*\d+(?:\s|$)|amendment\b|draft\b|ind\s*(?:#|no\.?|:)?\s*:?\s*\d|ide\s*(?:#|no\.?|:)?\s*:?\s*g?\d|(?:ind|ide)(?:\s*\/\s*(?:ind|ide))?(?![-a-z0-9]))/i;

/**
 * A bare dotted version ("1.6", "1.0.2"), alone or followed by a date
 * ("2.0, 12 Jan 2026", "3.1 (12 January 2026)", "2.0 dated ..."). A real
 * protocol number is an identifier, never a bare decimal.
 */
const BARE_VERSION = /^\s*\d+(?:\.\d+)+\s*(?:$|[,(;]|dated\b|\d{1,2}\s+[A-Za-z]{3,})/i;

/**
 * A registry or regulator identifier, never a protocol number: a
 * ClinicalTrials.gov NCT number, a EudraCT number (YYYY-NNNNNN-NN) or an EU
 * CTIS number (YYYY-5NNNNN-NN-NN). Added 2026-10-01 with the protocol-number
 * extraction field, whose prompt names these as not the protocol number.
 * The id may carry its label: "EudraCT No. 2023-001234-56", "EU CT ...",
 * "CTIS ...", "ClinicalTrials.gov NCT01234567", "NCT: NCT01234567" (an
 * optional "No.", "Number", "Identifier", "#" and ":" after the label), or
 * the registry label after the id in brackets ("NCT01234567
 * (ClinicalTrials.gov)"), and may end in a full stop or comma. A code that
 * only starts with a label ("NCT-FOLLOWUP-01") is not an id and is kept.
 */
const REGISTRY_ID =
  /^\s*(?:(?:EudraCT|EU[\s-]?CT|CTIS|ClinicalTrials\.gov|NCT|IND|IDE)\b\.?\s*(?:No\.?|Number|Identifier|#)?\s*:?\s*)?(?:NCT\s*\d{8}|\d{4}-\d{6}-\d{2}(?:-\d{2})?)(?:\s*\(\s*(?:EudraCT|EU[\s-]?CT|CTIS|ClinicalTrials\.gov)\s*\))?\s*[.,]?\s*$/i;

/**
 * A protocol-number candidate, or "" when it is not one.
 *
 * `analyzeProtocol.ts` fills `studySetup.protocolNumber` automatically, and
 * until it is corrected it fills it from the IND/IDE number or, failing that,
 * from the protocol VERSION. So a "user" protocol number may be a version
 * string ("Version Number: 3.0") that no person ever typed. A value equal to
 * the study's own version or IND/IDE number, or one that reads as a version
 * or an IND/IDE number, is refused here, and the number is then TBD rather
 * than wrong.
 */
export function protocolNumberCandidate(
  v: unknown,
  notThese: Array<unknown> = [],
): string {
  const s = cleanProtocolField(v);
  if (!s) return "";
  if (NOT_A_PROTOCOL_NUMBER.test(s) || BARE_VERSION.test(s) || REGISTRY_ID.test(s)) return "";
  const fold = (x: unknown) => cleanProtocolField(x).toLowerCase();
  if (notThese.some((x) => fold(x) !== "" && fold(x) === s.toLowerCase())) return "";
  return s;
}

/**
 * The study's protocol number, from the protocol-number fields only: Study
 * Settings, then the project document, then the protocol's own field. Never
 * the IND/IDE number and never the protocol version, including when one of
 * those was auto-filled into a protocol-number field. "" when there is none,
 * so the document says TBD (or nothing) rather than printing a wrong value.
 * The same rule as studyProfile's protocolNumberFact and protocolContext.
 */
export function resolveProtocolNumber(src: {
  studySetup?: unknown;
  analysis?: unknown;
  projectData?: unknown;
}): string {
  const rec = (v: unknown): Record<string, unknown> =>
    v && typeof v === "object" ? (v as Record<string, unknown>) : {};
  const ss = rec(src.studySetup);
  const a = rec(src.analysis);
  const pd = rec(src.projectData);
  const notThese = [a.protocolVersion, a.ideOrIndNumber, ss.indIdeNumber];
  return (
    protocolNumberCandidate(ss.protocolNumber, notThese) ||
    protocolNumberCandidate(pd.protocolNumber, notThese) ||
    protocolNumberCandidate(a.protocolNumber, notThese)
  );
}

/**
 * The protocol's own protocol number, as extracted (`analysis.protocolNumber`,
 * added to the extraction schema 2026-10-01), guarded: never the IND/IDE
 * number, the NCT, EudraCT or CTIS number, or the protocol version. The
 * protocol agent stores this value, and analyzeProtocol may auto-fill Study
 * Settings from it. "" when the protocol states none (and for every analysis
 * made before the field existed).
 */
export function autoFilledProtocolNumber(analysis: Record<string, unknown> | null | undefined): string {
  const a = analysis ?? {};
  return protocolNumberCandidate(a.protocolNumber, [a.protocolVersion, a.ideOrIndNumber, a.nctNumber]);
}

export function cleanProtocolField(v: unknown): string {
  if (typeof v !== "string") return "";
  const trimmed = v.trim();
  if (!trimmed) return "";
  if (isPlaceholderValue(trimmed)) return "";
  return trimmed;
}
